// features/videos/real/realItems.ts — a real video post as the card shape (VideoItem +
// creator) the Videos components already draw, so real videos and labelled samples look
// identical apart from the poster and the "Sample" badge.

import type { Post } from '@/core/models/post';
import type { VideoCreator, VideoItem } from '@/core/models/video';

/** Title rule (owner): the uploader's title, else the post's first line. */
export function videoTitle(post: Pick<Post, 'body' | 'video'>): string {
  const own = post.video?.title?.trim();
  if (own) return own;
  const firstLine =
    post.body
      .split('\n')
      .find((line) => line.trim())
      ?.trim() ?? '';
  if (!firstLine) return 'Untitled video';
  return firstLine.length > 100 ? `${firstLine.slice(0, 99)}…` : firstLine;
}

/** The post text minus a first line that was promoted to the title. */
export function videoDescription(post: Pick<Post, 'body' | 'video'>): string {
  if (post.video?.title?.trim()) return post.body.trim();
  const lines = post.body.split('\n');
  const first = lines.findIndex((line) => line.trim());
  return first === -1
    ? ''
    : lines
        .slice(first + 1)
        .join('\n')
        .trim();
}

export function toVideoItem(post: Post): VideoItem | null {
  const video = post.video;
  if (!video) return null;
  return {
    id: post.id,
    title: videoTitle(post),
    description: videoDescription(post),
    authorId: post.authorId,
    category: video.category ?? null,
    tags: [],
    durationSec: Math.round(video.durationMs / 1000),
    views: video.views ?? 0,
    ageLabel: post.time,
    ts: post.createdAt ? Date.parse(post.createdAt) : 0,
    short: Boolean(video.isShort),
    posterUrl: video.posterUrl,
    ...(post.watchProgress
      ? {
          progress: post.watchProgress.watched
            ? 1
            : Math.min(1, post.watchProgress.positionMs / Math.max(1, video.durationMs)),
        }
      : {}),
  };
}

export function toCreator(post: Post): VideoCreator {
  return {
    id: post.authorId,
    name: post.name,
    handle: post.handle,
    av: post.av,
    verified: post.verified,
    followerCount: 0,
  };
}

/** Cards + a creator lookup for a list of posts (non-video posts are skipped). */
export function toCards(posts: Post[]): {
  items: VideoItem[];
  creators: Record<string, VideoCreator>;
} {
  const items: VideoItem[] = [];
  const creators: Record<string, VideoCreator> = {};
  for (const post of posts) {
    const item = toVideoItem(post);
    if (!item) continue;
    items.push(item);
    creators[post.authorId] = toCreator(post);
  }
  return { items, creators };
}
